import React from 'react'
import { constraintConfig } from '../../../constraintConfig'

export type LoginSourceMeta = {
    label: string
    icon: string
}

export const getLoginSourceMeta = (sourceId?: number | null): LoginSourceMeta | null => {
    if (sourceId === constraintConfig.recordSourceId.WebApp) {
        return { label: 'Web', icon: '/media/icons/figmaIcons/Web.svg' }
    }
    if (sourceId === constraintConfig.recordSourceId.Mobile) {
        return { label: 'Mobile', icon: '/media/icons/figmaIcons/Mobile.svg' }
    }
    return null
}

export const loginSourceIconHtml = (sourceId?: number | null) => {
    const meta = getLoginSourceMeta(sourceId)
    if (!meta) return ''
    return `<span class="ss-source-icon-chip ss-presence-source-chip"><img src="${meta.icon}" alt="${meta.label}" width="16" height="16" /></span>`
}

export const buildPresenceTooltipHtml = ({
    isOnline,
    dateTimeLabel,
    sourceId,
}: {
    isOnline: boolean
    dateTimeLabel: string
    sourceId?: number | null
}) => {
    const icon = loginSourceIconHtml(sourceId)
    const sourceLabel = getLoginSourceMeta(sourceId)?.label
    const heading = isOnline ? 'Last Login' : 'Last Leave'
    const sourceSuffix = sourceLabel ? ` (${sourceLabel})` : ''
    const datePart = dateTimeLabel?.trim() || ''

    if (!datePart && !icon) {
        return `<div class="ss-presence-tooltip"><strong>${heading}</strong></div>`
    }

    const label = datePart
        ? `<strong>${heading}${sourceSuffix}:</strong> ${datePart}`
        : `<strong>${heading}${sourceSuffix}:</strong>`

    return `<div class="ss-presence-tooltip"><span class="ss-presence-tooltip__row">${icon}<span>${label}</span></span></div>`
}

type UserPresenceDotProps = {
    isOnline: boolean
    tooltipHtml: string
    tooltipId?: string
    variant?: 'avatar' | 'inline'
}

export const UserPresenceDot = ({
    isOnline,
    tooltipHtml,
    tooltipId = 'user-presence',
    variant = 'avatar',
}: UserPresenceDotProps) => {
    const baseClass = variant === 'inline' ? 'ss-user-online-dot-inline' : 'ss-user-online-dot'
    return (
        <span
            className={`${baseClass} ${isOnline ? 'is-online' : 'is-offline'}`}
            data-tooltip-id={tooltipId}
            data-tooltip-html={tooltipHtml}
            aria-label={isOnline ? 'User online' : 'User offline'}
            role="img"
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
            }}
        />
    )
}

type LoginSourceIconProps = {
    sourceId?: number | null
    tooltipId?: string
    showLabel?: boolean
}

export const LoginSourceIcon = ({ sourceId, tooltipId = 'user-login-source', showLabel = false }: LoginSourceIconProps) => {
    const meta = getLoginSourceMeta(sourceId)
    if (!meta) return showLabel ? <span className='ss-login-source-label'>—</span> : null
    return (
        <>
            <span
                className='ss-source-icon-chip'
                data-tooltip-id={tooltipId}
                data-tooltip-content={meta.label}
                aria-label={meta.label}
            >
                <img src={meta.icon} alt="" width={16} height={16} />
            </span>
            {showLabel && <span className='ss-login-source-label'>{meta.label}</span>}
        </>
    )
}

export const LoginSourceWithDate = ({
    sourceId,
    children,
}: {
    sourceId?: number | null
    children: React.ReactNode
}) => (
    <span className='d-inline-flex align-items-center gap-2 ss-login-source-cell'>
        <LoginSourceIcon sourceId={sourceId} showLabel />
        {children}
    </span>
)
